import React, { useEffect } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { usePostLoginMutation } from "@/store/rest_api";
import { useAuth } from "@/hooks/useAuth";
import { authService } from "@/services/authService";
import { getEmailValidationPattern } from "@/util/validation";
import { apiErrorMessage } from "@/util/apiError";
import { DEVICE_PLATFORM, getDeviceName, getDeviceToken, setDeviceToken } from "@/util/device";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import OAuthLoginButtons from "@/components/OAuthLoginButtons";
import LoginData from "../model/interface/LoginData";
import type { TwoFactorNavigationState } from "../model/interface/TwoFactor";

const CRM_SIGN_IN_ENABLED = false;

export default function LoginForm() {
  const navigate = useNavigate();
  const { refreshAuthStatus } = useAuth();
  const [searchParams] = useSearchParams();
  const requestedRedirect = searchParams.get("redirect");
  const redirect =
    requestedRedirect?.startsWith("/") && !requestedRedirect.startsWith("//") ? requestedRedirect : undefined;
  const [postLogin, postLoginResult] = usePostLoginMutation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = (data: FieldValues) => {
    postLogin({
      ...(data as LoginData),
      deviceToken: getDeviceToken() ?? undefined,
      deviceName: getDeviceName(),
      devicePlatform: DEVICE_PLATFORM,
    });
  };

  useEffect(() => {
    if (postLoginResult.isSuccess) {
      const data = postLoginResult.data;
      if ("twoFactorRequired" in data) {
        const navigationState: TwoFactorNavigationState = { ...data, redirect };
        navigate(data.enrollmentRequired ? "/login/enroll" : "/login/verify", {
          state: navigationState,
        });
        return;
      }
      const { accessToken, refreshToken, deviceToken } = data;
      authService.setAccessToken(accessToken);
      authService.setRefreshToken(refreshToken);
      if (deviceToken) {
        setDeviceToken(deviceToken);
      }
      refreshAuthStatus().then(() => navigate(redirect ?? "/dashboard"));
    }
  }, [postLoginResult, navigate, refreshAuthStatus, redirect]);

  return (
    <div className="w-full max-w-md space-y-6 text-foreground">
      <div className="flex justify-center">
        <Link to="/">
          <img src="/assets/images/retreevo-logo.svg" alt="" className="h-10 w-auto" />
        </Link>
      </div>

      <div className="rounded-xl border bg-card shadow-sm p-8 space-y-6">
        <div className="space-y-1 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">Welcome back</h1>
          <p className="text-sm text-muted-foreground">Sign in to your Retreevo workspace.</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@company.com"
              autoComplete="email"
              required
              {...register("email", {
                required: true,
                pattern: getEmailValidationPattern(),
              })}
            />
            {errors.email && (
              <p className="text-xs text-destructive">A valid email is required</p>
            )}
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <Link
                to="/forgot-password"
                className="text-xs text-muted-foreground hover:text-foreground hover:underline"
              >
                Forgot password?
              </Link>
            </div>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              {...register("password", { required: true })}
            />
            {errors.password && (
              <p className="text-xs text-destructive">Password is required</p>
            )}
          </div>

          {postLoginResult.isError && (
            <p className="text-sm text-destructive">
              {apiErrorMessage(postLoginResult.error, "Unable to sign in. Please try again.")}
            </p>
          )}

          <Button type="submit" className="w-full" disabled={postLoginResult.isLoading}>
            {postLoginResult.isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Sign in
          </Button>
        </form>

        {CRM_SIGN_IN_ENABLED && <OAuthLoginButtons redirect={redirect} />}

        <div className="text-center text-sm text-muted-foreground">
          Don't have an account?{" "}
          <Link to="/register" className="font-medium text-foreground hover:text-primary">
            Get started free
          </Link>
        </div>
      </div>

      <p className="text-center text-xs text-muted-foreground">
        By signing in, you agree to our{" "}
        <a href="/terms-of-use" className="underline hover:text-foreground">
          Terms of Service
        </a>{" "}
        and{" "}
        <a href="/privacy-policy" className="underline hover:text-foreground">
          Privacy Policy
        </a>
        .
      </p>
    </div>
  );
}
